import React from 'react';
import { Link } from 'react-router-dom';
import { cva, type VariantProps } from 'class-variance-authority';
import KoobTooltip from './KoobTooltip';
import { icons, type KoobIconName } from '../icons';

export type KoobTableActionIcon = KoobIconName;

/** Shared with KoobTableActionMenu so its trigger looks like any action. */
export const tableActionVariants = cva(
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border-none bg-transparent p-0 text-base cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'text-muted-foreground hover:bg-accent hover:text-foreground',
        danger: 'text-red-500 hover:bg-red-500/10 hover:text-red-600',
        warning: 'text-orange-500 hover:bg-orange-500/10 hover:text-orange-600',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export const resolveTableActionIcon = (
  icon: KoobTableActionIcon | React.ReactNode,
): React.ReactNode =>
  typeof icon === 'string' && icon in icons ? (
    <i className={icons[icon as KoobIconName]} />
  ) : (
    icon
  );

type KoobTableActionProps = VariantProps<typeof tableActionVariants> & {
  /** A preset name (standardized icon) or a custom icon node. */
  icon: KoobTableActionIcon | React.ReactNode;
  /** Accessible name; shown as the tooltip. */
  label: string;
  /** Renders a react-router Link instead of a button. */
  to?: React.ComponentProps<typeof Link>['to'];
  /** Router state forwarded to the Link. */
  state?: unknown;
  onClick?: React.MouseEventHandler;
  disabled?: boolean;
  'data-testid'?: string;
};

const KoobTableAction = React.forwardRef<
  HTMLButtonElement & HTMLAnchorElement,
  KoobTableActionProps
>(({ icon, label, to, state, onClick, disabled, variant, ...props }, ref) => {
  const renderedIcon = resolveTableActionIcon(icon);
  const className = tableActionVariants({ variant });
  // actions live in clickable rows: never let a click bubble to onRowClick
  const handleClick: React.MouseEventHandler = (event) => {
    event.stopPropagation();
    onClick?.(event);
  };

  // a disabled action must stay a button: <a> has no native disabled
  const inner =
    to != null && !disabled ? (
      <Link
        ref={ref}
        to={to}
        state={state}
        aria-label={label}
        className={className}
        onClick={handleClick}
        {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {renderedIcon}
      </Link>
    ) : (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        disabled={disabled}
        className={className}
        onClick={handleClick}
        {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        {renderedIcon}
      </button>
    );

  return <KoobTooltip tip={label}>{inner}</KoobTooltip>;
});

KoobTableAction.displayName = 'KoobTableAction';

export default KoobTableAction;
